import { useMemo } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Stack, alpha, CircularProgress,
  Chip,
} from '@mui/material';
import { Search, Inbox, QueuePlayNext } from '@mui/icons-material';
import DispatchJobCard from './DispatchJobCard';
import { DispatchRow, QUEUE_STATUS_TABS, STATUS_BY_TAB, QueueStatusTab, rowNeedsAssignment } from './dispatchTypes';
import { DISPATCH, dispatchScroll } from './dispatchTheme';

interface DispatchQueuePanelProps {
  rows: DispatchRow[];
  loading?: boolean;
  selectedId?: string | null;
  statusTab: QueueStatusTab;
  onStatusTabChange: (tab: QueueStatusTab) => void;
  search: string;
  onSearchChange: (q: string) => void;
  onSelect: (row: DispatchRow) => void;
}

export default function DispatchQueuePanel({
  rows,
  loading,
  selectedId,
  statusTab,
  onStatusTabChange,
  search,
  onSearchChange,
  onSelect,
}: DispatchQueuePanelProps) {
  const counts = useMemo(() => {
    const byStatus: Record<string, number> = {};
    QUEUE_STATUS_TABS.forEach((t) => {
      byStatus[t.key] = t.key === 'action'
        ? rows.filter((r) => rowNeedsAssignment(r)).length
        : rows.filter((r) => STATUS_BY_TAB[t.key].includes(r.status.toLowerCase())).length;
    });
    return byStatus;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (!STATUS_BY_TAB[statusTab].includes(r.status.toLowerCase())) return false;
        if (!q) return true;
        return (
          r.ref.toLowerCase().includes(q)
          || r.customer.toLowerCase().includes(q)
          || r.customerPhone.includes(q)
          || r.partner.toLowerCase().includes(q)
          || (r.pickupAddress || '').toLowerCase().includes(q)
          || (r.dropoffAddress || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [rows, statusTab, search]);

  const totalInTab = counts[statusTab] || 0;

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DISPATCH.bgMuted,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          borderBottom: `1px solid ${DISPATCH.border}`,
          background: `linear-gradient(135deg, ${alpha('#4f46e5', 0.08)} 0%, transparent 100%)`,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} mb={1.25} minWidth={0}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(DISPATCH.primary, 0.1),
              color: DISPATCH.primary,
              flexShrink: 0,
            }}
          >
            <QueuePlayNext fontSize="small" />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2} noWrap>Job Queue</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {filtered.length === totalInTab
                ? `${totalInTab} job${totalInTab !== 1 ? 's' : ''} · ${
                  statusTab === 'action' ? 'tap to assign' : statusTab === 'active' ? 'in progress' : 'in this queue'
                }`
                : `${filtered.length} of ${totalInTab} shown`}
            </Typography>
          </Box>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder="Search ref, customer, phone, address…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(DISPATCH.primary, 0.03) },
          }}
        />
      </Box>

      {/* Status filters — 2×2 grid, no horizontal scroll */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 0.75,
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${DISPATCH.border}`,
          flexShrink: 0,
        }}
      >
        {QUEUE_STATUS_TABS.map((t) => {
          const count = counts[t.key] || 0;
          const active = statusTab === t.key;
          return (
            <Chip
              key={t.key}
              label={`${t.shortLabel} (${count})`}
              size="small"
              onClick={() => onStatusTabChange(t.key)}
              sx={{
                fontWeight: 700,
                cursor: 'pointer',
                height: 30,
                width: '100%',
                maxWidth: '100%',
                borderRadius: 2,
                justifyContent: 'center',
                '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                bgcolor: active ? alpha(t.color, 0.12) : 'transparent',
                color: active ? t.color : 'text.secondary',
                border: 1,
                borderColor: active ? alpha(t.color, 0.35) : 'divider',
                '&:hover': { bgcolor: alpha(t.color, 0.08) },
              }}
            />
          );
        })}
      </Box>

      {/* List — vertical scroll only */}
      <Box sx={{ ...dispatchScroll, p: 1.25 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            textAlign="center"
            py={6}
            px={2}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2.5,
              bgcolor: 'background.paper',
            }}
          >
            <Inbox sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" fontWeight={700} color="text.secondary">No jobs in this queue</Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              {statusTab === 'action' ? 'All caught up — nothing needs action right now' : 'Try another status filter'}
            </Typography>
          </Box>
        ) : (
          filtered.map((row, index) => (
            <DispatchJobCard
              key={`${row.service}-${row._id}`}
              row={row}
              serialNo={index + 1}
              selected={selectedId === `${row.service}-${row._id}`}
              onClick={() => onSelect(row)}
            />
          ))
        )}
      </Box>
    </Box>
  );
}
